/* ==========================================================================
   Class 5 Performance — global.js
   Minimal vanilla JS for global UI: light/dark toggle, mobile nav, qty steppers.
   No frameworks. Loaded with `defer`.

   NOTE: the no-FOUC mode init runs INLINE in <head> (layout/theme.liquid),
   before paint. This file only handles the user-initiated toggle afterward.

   Hooks (add these attributes in markup to wire behavior):
     [data-theme-toggle]  -> flips <html data-theme>, persists to localStorage
     [data-nav-toggle]    -> opens mobile nav drawer (#mobile-nav)
     [data-cart-toggle]   -> opens the AJAX cart drawer (#cart-drawer)
     [data-drawer-close]  -> closes the nearest open drawer
     .overlay#site-overlay-> shared dimming overlay
     .qty                 -> qty stepper container with __btn[data-qty-dir]
                             ("up"/"down") and .qty__input

   AJAX cart (Shopify Cart API + Section Rendering API):
     [data-cart-drawer]        -> the drawer dialog (#cart-drawer)
     [data-cart-drawer-body]   -> region swapped with re-rendered section HTML
     [data-cart-count]         -> header badge, updated from cart.item_count
     [data-cart-qty]/[input]   -> per-line qty controls (change.js)
     [data-cart-remove]        -> per-line remove (change.js qty 0)
     .pdp-form (form[action*="/cart/add"]) -> intercepted -> /cart/add.js
   ========================================================================== */
(function () {
  "use strict";

  var body = document.body;
  var root = document.documentElement;
  var overlay = document.getElementById("site-overlay");

  /* ---- Light / dark mode toggle ----
     The correct mode is already on <html> from the inline head script.
     Here we sync toggle buttons' state and flip + persist on click. */
  var THEME_KEY = "c5-theme";

  function currentTheme() {
    return root.getAttribute("data-theme") === "light" ? "light" : "dark";
  }

  function syncToggleState() {
    var isLight = currentTheme() === "light";
    document.querySelectorAll("[data-theme-toggle]").forEach(function (btn) {
      btn.setAttribute("aria-pressed", isLight ? "true" : "false");
      btn.setAttribute(
        "aria-label",
        isLight ? "Switch to dark mode" : "Switch to light mode"
      );
      var label = btn.querySelector("[data-theme-label]");
      if (label) label.textContent = isLight ? "Dark mode" : "Light mode";
    });
  }

  function setTheme(theme) {
    root.setAttribute("data-theme", theme);
    try { localStorage.setItem(THEME_KEY, theme); } catch (e) {}
    syncToggleState();
  }

  syncToggleState();

  document.addEventListener("click", function (e) {
    var toggle = e.target.closest("[data-theme-toggle]");
    if (!toggle) return;
    e.preventDefault();
    setTheme(currentTheme() === "light" ? "dark" : "light");
  });

  var lastTrigger = null;       // element to return focus to on close
  var FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function focusFirst(drawer) {
    var el = drawer.querySelector(FOCUSABLE);
    if (el) el.focus();
  }

  function openDrawer(drawer, trigger) {
    if (!drawer) return;
    lastTrigger = trigger || null;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    drawer.removeAttribute("inert"); // re-enable focus + a11y tree while open
    if (overlay) overlay.classList.add("is-open");
    body.classList.add("no-scroll");
    focusFirst(drawer);
  }

  function closeDrawers() {
    var hadOpen = false;
    document.querySelectorAll(".drawer.is-open").forEach(function (d) {
      d.classList.remove("is-open");
      d.setAttribute("aria-hidden", "true");
      d.setAttribute("inert", ""); // remove offscreen drawer from focus order + a11y tree
      hadOpen = true;
    });
    if (overlay) overlay.classList.remove("is-open");
    body.classList.remove("no-scroll");
    if (hadOpen && lastTrigger && typeof lastTrigger.focus === "function") {
      lastTrigger.focus();      // return focus to the trigger (a11y)
    }
    lastTrigger = null;
  }

  /* ---- Mobile nav + cart drawer toggles ---- */
  document.addEventListener("click", function (e) {
    var navBtn = e.target.closest("[data-nav-toggle]");
    if (navBtn) {
      e.preventDefault();
      openDrawer(document.getElementById("mobile-nav"), navBtn);
      return;
    }

    var cartBtn = e.target.closest("[data-cart-toggle]");
    if (cartBtn) {
      e.preventDefault();
      openDrawer(document.getElementById("cart-drawer"), cartBtn);
      return;
    }

    if (e.target.closest("[data-drawer-close]") ||
        (overlay && e.target === overlay)) {
      closeDrawers();
    }
  });

  /* Esc to close + focus trap (Tab cycles within the open drawer). */
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") { closeDrawers(); return; }
    if (e.key !== "Tab") return;
    var open = document.querySelector(".drawer.is-open");
    if (!open) return;
    var nodes = Array.prototype.filter.call(
      open.querySelectorAll(FOCUSABLE),
      function (n) { return n.offsetParent !== null; }
    );
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---- Quantity steppers ---- */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".qty__btn[data-qty-dir]");
    if (!btn) return;
    var wrap = btn.closest(".qty");
    var input = wrap && wrap.querySelector(".qty__input");
    if (!input) return;
    var min = parseInt(input.getAttribute("min"), 10);
    if (isNaN(min)) min = 1;
    var val = parseInt(input.value, 10);
    if (isNaN(val)) val = min;
    val += btn.getAttribute("data-qty-dir") === "up" ? 1 : -1;
    if (val < min) val = min;
    input.value = val;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ========================================================================
     AJAX CART (Shopify Cart API + Section Rendering API)
     ====================================================================== */
  var DRAWER_SECTION = "cart-drawer";

  function drawerEl() { return document.getElementById("cart-drawer"); }

  function setLoading(on) {
    var d = drawerEl();
    if (d) d.classList.toggle("is-loading", !!on);
  }

  // Update the header count badge from a cart object's item_count.
  function updateCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      if (count > 0) { el.removeAttribute("hidden"); }
      else { el.setAttribute("hidden", ""); }
      var btn = el.closest("[data-cart-toggle]");
      if (btn) btn.setAttribute("aria-label", "Cart (" + count + ")");
    });
  }

  // Re-render the drawer via the Section Rendering API and swap its body/foot.
  function refreshDrawer() {
    return fetch("?sections=" + DRAWER_SECTION, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[DRAWER_SECTION];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var fresh = parsed.querySelector("[data-cart-drawer]");
        var live = drawerEl();
        if (fresh && live) {
          // Replace only the inner markup so the open state / element identity hold.
          live.innerHTML = fresh.innerHTML;
        }
      });
  }

  function cartChange(payload) {
    setLoading(true);
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCount(cart.item_count);
        return refreshDrawer();
      })
      .catch(function () { /* leave drawer as-is on failure */ })
      .then(function () { setLoading(false); });
  }

  // Per-line qty input change (drawer). min=0 removes the line.
  document.addEventListener("change", function (e) {
    var input = e.target.closest("[data-cart-qty-input]");
    if (!input) return;
    var line = parseInt(input.getAttribute("data-cart-qty-input"), 10);
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    cartChange({ line: line, quantity: qty });
  });

  // Per-line remove (drawer) -> set qty 0.
  document.addEventListener("click", function (e) {
    var rm = e.target.closest("[data-cart-remove]");
    if (!rm) return;
    e.preventDefault();
    var line = parseInt(rm.getAttribute("data-cart-remove"), 10);
    cartChange({ line: line, quantity: 0 });
  });

  // PDP add-to-cart: intercept the product form, POST /cart/add.js, open drawer.
  document.addEventListener("submit", function (e) {
    var form = e.target.closest("form.pdp-form");
    if (!form) return;
    // Only intercept the add action (let dynamic checkout / other submits pass).
    e.preventDefault();
    setLoading(true);
    var btn = form.querySelector("[data-pdp-add]");
    if (btn) btn.setAttribute("disabled", "");
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) throw new Error("add failed");
        return r.json();
      })
      .then(function () { return fetch("/cart.js"); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCount(cart.item_count);
        return refreshDrawer();
      })
      .then(function () {
        setLoading(false);
        if (btn) btn.removeAttribute("disabled");
        openDrawer(drawerEl(), btn);
      })
      .catch(function () {
        // Hard fallback: let the browser do a normal submit to /cart.
        setLoading(false);
        if (btn) btn.removeAttribute("disabled");
        form.submit();
      });
  });
})();
